import React, { useCallback, useMemo } from "react";
import { SlideoutDrawerV2, Link, Text, LoadingIndicator, Flex } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useGetDistributionBatchForSlideoutQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { DistributionBatchRowItem } from "../DistributionBatchesTable/types";
import { toDistributionBatchRowItem } from "../DistributionBatchesTable/utils";
import { PaymentsContextProvider } from "../PaymentsListWorkflow/Context/PaymentsContext";
import { PaymentDetails } from "../PaymentsListWorkflow/PaymentDetails";
import { PaymentBatchesTable } from "./PaymentBatchesTable";
import { slideoutContainer, slideoutBody } from "./styles_vanilla.css";
import { TransactionDetailsCard } from "./TransactionDetailsCard";
import { DistributionSlideoutProps } from "./types";

/**
 * Transforms the GraphQL query result into a DistributionBatchRowItem.
 */
const transformQueryResultToDistribution = (
  data: ReturnType<typeof useGetDistributionBatchForSlideoutQuery>["data"],
): DistributionBatchRowItem | null => {
  const distributionBatch = data?.distributionBatch;
  if (!distributionBatch) return null;

  return toDistributionBatchRowItem(distributionBatch);
};

export const DistributionSlideout: React.FC<DistributionSlideoutProps> = ({
  onReviewClick,
  isOpen,
  onClose,
  distributionBatchId,
  onCreatePayment,
  selectedPaymentBatchId,
  onReviewSlideoutClose,
  paymentsContext,
}) => {
  // Fetch distribution data when the slideout is open.
  // Using network-only to ensure we always get fresh data when opening the slideout,
  // since payment status may have changed since the table was loaded.
  const queryResult = useGetDistributionBatchForSlideoutQuery({
    variables: { distributionBatchId },
    context: clientContext,
    skip: !isOpen,
    fetchPolicy: "network-only",
  });

  const distribution = transformQueryResultToDistribution(queryResult.data);
  const distributionName = distribution?.description || "Distribution";

  const entityUrl = useMemo<string | null>(
    () =>
      distribution?.entityId
        ? `https://${distribution.jsqHostName}/account/${distribution.entityId}`
        : null,
    [distribution],
  );

  const distributionUrl = useMemo<string | null>(() => {
    return distribution?.id
      ? `https://${distribution.jsqHostName}/distribution_batch/${distribution.id}`
      : null;
  }, [distribution]);

  // Show loading state when fetching data
  const isLoading = queryResult.loading;

  const handleCreatePayment = useCallback(() => {
    if (distribution && onCreatePayment) {
      onCreatePayment(distribution);
    }
  }, [distribution, onCreatePayment]);

  const slideoutContent = (
    <SlideoutDrawerV2
      // @ts-expect-error - OverlayStyle enum properly exported but auto-formatter removes import
      overlayStyle="transparent"
      isOpen={isOpen}
      onClose={onClose}
      data-testid="distribution-slideout"
    >
      <div className={slideoutContainer}>
        {isLoading ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
            style={{ padding: "48px", backgroundColor: Colors.Neutral0 }}
          >
            <LoadingIndicator />
          </Flex>
        ) : (
          <>
            <SlideoutDrawerV2.ComplexHeader
              title={distributionName ?? ""}
              subtitles={[
                distributionUrl ? (
                  <Text style={{ color: Colors.Neutral600 }}>
                    Distribution ·{" "}
                    <Link
                      href={distributionUrl}
                      align="left"
                      icon="external-link"
                      iconPosition="right"
                      shouldOpenInNewTab
                      style={{ display: "inline" }}
                    >
                      Transaction details
                    </Link>
                  </Text>
                ) : (
                  "Distribution"
                ),
              ]}
            />
            <div className={slideoutBody}>
              {distribution && (
                <TransactionDetailsCard
                  distribution={distribution}
                  entityUrl={entityUrl ? entityUrl : undefined}
                  onCreatePayment={onCreatePayment ? handleCreatePayment : undefined}
                />
              )}
              <PaymentBatchesTable
                distributionBatchId={distributionBatchId}
                onReviewClick={onReviewClick}
              />
            </div>
          </>
        )}
      </div>
    </SlideoutDrawerV2>
  );

  // Review slideout for payment batch details
  // PaymentDetails now includes its own SlideoutDrawerV2 wrapper
  const reviewSlideoutContent =
    paymentsContext && selectedPaymentBatchId ? (
      <PaymentDetails
        paymentBatchId={selectedPaymentBatchId}
        onClose={onReviewSlideoutClose ?? (() => {})}
      />
    ) : null;

  // Wrap in PaymentsContextProvider if paymentsContext is provided
  if (paymentsContext) {
    return (
      <PaymentsContextProvider value={paymentsContext}>
        {slideoutContent}
        {reviewSlideoutContent}
      </PaymentsContextProvider>
    );
  }

  return slideoutContent;
};

export default DistributionSlideout;
